import React, { useEffect, useRef, useState } from "react";
import PageLoader from "../../../shared/PageLoader";
import { useAppSelector } from "../../../../redux/hook";
import { useExportEmployeesLeaveCredit } from "../../../../backend/exports";
import { downloadFileContent } from "../../../../utils/common";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { toast } from "react-toastify";
import { getEmployeesLeaveCredit } from "../../../../redux/features/performerClient.slice";
import EmployeeUploadFilters from "../shared/EmployeeUploadFilters";
import EmployeeUploadSearch from "../../../common/EmployeeUploadSearch";
import AdditionalFiltersInputUploads from "../shared/AdditionalFiltersInputUploads";
import {
  useEmployeeUploadFilters,
  useEmployeeUploadSearchConfig,
  useEmployeeUploadData,
  usePerformerClientCompanyData,
  useEmployeeUploadCompanyAutoSelect,
} from "../../../../hooks/useEmployeeUploads";
import {
  getEmployeeUploadCompanyNameById,
  getEmployeeUploadAssocCompanyNameById,
  getEmployeeUploadLocationById,
  buildEmployeeUploadQueryPayload,
} from "../../../../utils/employeeUploads.utils";
import { UploadButton } from "../shared";
import PerformerTable from "../shared/PerformerTable";
import "../shared/performer-client.css";
import Icon from "../../../common/Icon";


const EmployeeLeaveCreditUpload = () => {

  // Redux selectors
  const employeesLeaveCreditDetails = useAppSelector(
    (state) => state.performerClient.employeesLeaveCreditDetails,
  );

  // Use custom hooks
  const { filters, updateFilter, updateMultipleFilters } =
    useEmployeeUploadFilters();
  const { searchConfig, updateSearchConfig } = useEmployeeUploadSearchConfig({
    activeSort: "created_at",
    sortType: "desc",
  });
  const { fetchData, fetchWithSearch } = useEmployeeUploadData({
    monthFieldName: "wage_month_eq",
    searchFieldName: "employee_code_cont",
  });
  const {
    companies,
    associateCompanies,
    locations,
    companiesLoading,
    associateCompaniesLoading,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations,
  } = usePerformerClientCompanyData();

  // Establishment Type rests at "All" (blank) on this page -- only Company
  // auto-selects, first alphabetically, once the list resolves. Also keeps
  // the navbar/sidenav branding in sync with the resolved company/AC
  // (bindLogo defaults to true).
  useEmployeeUploadCompanyAutoSelect(filters, updateFilter, companies, companiesLoading, associateCompanies, associateCompaniesLoading);

  // Local state for UI components
  const [companyName, setCompanyName] = useState("");
  const [associate_company_name, setAssociateCompanyName] = useState("");
  const [locationName, setLocationName] = useState("");
  const [companyLocationMappingId, setCompanyLocationMappingId] = useState("");
  const [showAdditionalFiltersModal, setShowAdditionalFiltersModal] =
    useState(false);
  const [selectedAdditionalFilters, setSelectedAdditionalFilters] =
    useState<any>({});

  const employeesLeaveCredit = employeesLeaveCreditDetails.data.items || [];
  const employeesLeaveCreditCount =
    employeesLeaveCreditDetails.data.meta.total_count;

  const { exportRecords, exporting } = useExportEmployeesLeaveCredit(
    (response: any) => {
      let filename =
        associate_company_name +
        "_" +
        locationName +
        "_" +
        "Employee-LeaveCredit" +
        "_" +
        filters.year +
        "_" +
        filters.month +
        ".xlsx";
      toast.success('Export will be sent to your registered email.');
    },
    () => {
      toast.error(ERROR_MESSAGES.DEFAULT);
    },
  );

  const loading = exporting || employeesLeaveCreditDetails.status === "loading";

  // Data fetching effects
  useEffect(() => {
    fetchCompanies();
    fetchData(getEmployeesLeaveCredit, filters, searchConfig);
  }, []);

  // Fetch data when filters change (including additional filters)
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    updateSearchConfig({ page: 0 });
    fetchData(getEmployeesLeaveCredit, filters, { ...searchConfig, page: 0 });
  }, [
    filters.establishmentType,
    filters.company,
    filters.associateCompany,
    filters.location,
    filters.year,
    filters.month,
    filters.employer,
    filters.employerLocation,
    filters.subContractor,
    filters.subContractorLocation,
  ]);

  useEffect(() => {
    if (filters.company) {
      fetchAssociateCompanies(filters.company);
      const selectedCompanyName = getEmployeeUploadCompanyNameById(
        filters.company,
        companies,
      );
      setCompanyName(selectedCompanyName);
    }
  }, [filters.company, companies]);

  useEffect(() => {
    if (filters.associateCompany) {
      fetchLocations(filters.associateCompany);
      const selectedCompanyName = getEmployeeUploadAssocCompanyNameById(
        filters.associateCompany,
        associateCompanies,
      );
      setAssociateCompanyName(selectedCompanyName);
    }
  }, [filters.associateCompany, associateCompanies]);

  useEffect(() => {
    if (filters.location) {
      const selectedLocationId = filters.location.split("^")[0];
      const selectedLocationName = getEmployeeUploadLocationById(
        selectedLocationId,
        locations,
      );
      setLocationName(selectedLocationName);
      const tempLocation =
        locations.find((loc: any) => loc.location_id === selectedLocationId) ||
        {};
      setCompanyLocationMappingId(tempLocation.company_location_mapping_id || "");
    }
  }, [filters.location, locations]);

  // Clear additional filters when parent filters change
  useEffect(() => {
    setSelectedAdditionalFilters({});
  }, [
    filters.establishmentType,
    filters.company,
    filters.associateCompany,
    filters.location,
  ]);

  // Handler functions - Filter updates are handled by the hook automatically

  const handleSearch = () => {
    updateSearchConfig({ page: 0 });
    fetchData(getEmployeesLeaveCredit, filters, { ...searchConfig, page: 0 });
  };

  const handleClearSearch = () => {
    updateSearchConfig({ searchInput: "" });
    fetchData(getEmployeesLeaveCredit, filters, { ...searchConfig, searchInput: "" });
  };

  const handleSort = (sortField: string) => {
    const newSortDirection =
      searchConfig.activeSort === sortField && searchConfig.sortType === "asc"
        ? "desc"
        : "asc";
    updateSearchConfig({
      activeSort: sortField,
      sortType: newSortDirection,
    });
    fetchData(
      getEmployeesLeaveCredit,
      filters,
      searchConfig,
      {},
      sortField,
      newSortDirection,
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchConfig({ page: newPage });
    fetchData(getEmployeesLeaveCredit, filters, {
      ...searchConfig,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchConfig({
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
    fetchData(getEmployeesLeaveCredit, filters, {
      ...searchConfig,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const handleAdditionalFiltersApply = (filterData: any) => {
    // Update filter state with additional filters
    updateMultipleFilters({
      employer: filterData.payload.employer_id_eq || "",
      employerLocation: filterData.payload.employer_location_id_eq || "",
      subContractor: filterData.payload.sub_contractor_id_eq || "",
      subContractorLocation:
        filterData.payload.sub_contractor_location_id_eq || "",
    });
    setSelectedAdditionalFilters(filterData.selectedFilters || {});
    setShowAdditionalFiltersModal(false);
  };

  const onClickExport = () => {
    const { per, page, ...payload } = buildEmployeeUploadQueryPayload(
      filters,
      searchConfig,
      {
        monthFieldName: "wage_month_eq",
        searchFieldName: "employee_code_cont",
      },
    );
    exportRecords(payload);
  };

  useEffect(() => {
    if (employeesLeaveCreditDetails.status === "succeeded") {
    } else if (employeesLeaveCreditDetails.status === "failed") {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [employeesLeaveCreditDetails.status]);


  return (
    <>
    <div className="performerclient-wrapper h-100">
      <div className="performer-client-page h-100 py-3">
        <div style={{ height: "100%" }}>
          <div className="pageHeading mb-0" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h4 className="mb-0">Employee Leave Credit</h4>
            <EmployeeUploadSearch
              value={searchConfig.searchInput}
              placeholder="Search (Employee Code)"
              onChange={(e) => updateSearchConfig({ searchInput: e.target.value })}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />
          </div>
          <div className="filters" style={{ padding: "16px 20px" }}>
            <div style={{ backgroundColor: "#ffffff", padding: "16px 16px", borderRadius: "8px" }}>
              <EmployeeUploadFilters
                filters={filters}
                companies={companies}
                associateCompanies={associateCompanies}
                locations={locations}
                onUpdateFilter={updateFilter}
                fetchAssociateCompanies={fetchAssociateCompanies}
                showYearMonth={true}
                showAdditionalFilters={true}
                onAdditionalFiltersClick={() => {
                  if (
                    filters.company &&
                    filters.associateCompany &&
                    filters.location
                  ) {
                    setShowAdditionalFiltersModal(true);
                  } else {
                    toast.error(
                      "Please select Company, Associate Company and Location to apply additional filters",
                    );
                  }
                }}
                actions={
                  <>
                    <div style={{ cursor: (!employeesLeaveCredit || !filters.company || !filters.associateCompany) ? "not-allowed" : "pointer" }}>
                      <button
                        onClick={onClickExport}
                        disabled={
                          !employeesLeaveCredit ||
                          !filters.company ||
                          !filters.associateCompany
                        }
                        className="download-btn"
                        title='Export Data'
                      >
                        <Icon name="downloadIcon" />
                      </button>
                    </div>
                    <UploadButton
                      configType="Leave Credit"
                      filters={{
                        company: filters.company,
                        associateCompany: filters.associateCompany,
                        establishmentType: filters.establishmentType,
                        location: filters.location ? filters.location.split("^")[0] : "",
                      }}
                      selectedAdditionalFilters={selectedAdditionalFilters}
                      onUploadSuccess={() =>
                        fetchData(getEmployeesLeaveCredit, filters, searchConfig)
                      }
                    />
                  </>
                }
              />
            </div>
          </div>

          {/* Additional Filters Modal */}
          {showAdditionalFiltersModal && (
            <AdditionalFiltersInputUploads
              data={{
                establishmentType: filters.establishmentType,
                company: filters.company,
                associate_company: filters.associateCompany,
                location: companyLocationMappingId,
              }}
              selectedFilters={selectedAdditionalFilters}
              onSubmit={handleAdditionalFiltersApply}
              onCancel={() => setShowAdditionalFiltersModal(false)}
            />
          )}

          <div style={{ padding: "0 20px" }}>
            <PerformerTable
              columns={[
                { label: "Employee Code", sortKey: "employee_code", render: (row) => row.employee_code },
                ...(filters.establishmentType === "CLRA" ? [{ label: "Company Employer Sub-Contractor Code", render: (row: any) => (row.company_employer_sub_contractor || {}).company_employer_sub_contractor_code }] : []),
                { label: "Wage Year", sortKey: "wage_month", render: (row) => row.year },
                { label: "Wage Month", sortKey: "wage_month", render: (row) => row.wage_month },
                { label: "PL-EL-AL Credit", render: (row) => row.pl_el_al_monthly_credit },
                { label: "PL-EL-AL Closing", render: (row) => row.pl_el_al_closing_balance },
                { label: "PL-EL-AL Opening", render: (row) => row.pl_el_al_opening_balance },
                { label: "CL Monthly Credit", render: (row) => row.cl_monthly_credit },
                { label: "CL Closing Balance", render: (row) => row.cl_closing_balance },
                { label: "CL Opening Balance", render: (row) => row.cl_opening_balance },
                { label: "ML Closing Balance", render: (row) => row.ml_closing_balance },
                { label: "ML Opening Balance", render: (row) => row.ml_opening_balance },
                { label: "SL Monthly Credit", render: (row) => row.sl_monthly_credit },
                { label: "SL Opening Balance", render: (row) => row.sl_opening_balance },
                { label: "SL Closing Balance", render: (row) => row.sl_closing_balance },
              ]}
              data={employeesLeaveCredit}
              count={employeesLeaveCreditCount}
              page={searchConfig.page}
              rowsPerPage={searchConfig.rowsPerPage}
              activeSort={searchConfig.activeSort}
              sortType={searchConfig.sortType}
              onSort={handleSort}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </div>
      </div>
    </div>
    {loading && <PageLoader>Loading...</PageLoader>}
    </>
  );
};

export default EmployeeLeaveCreditUpload;
